// Product form pieces for the optional field groups: brand, attribute fields
// (pharmacy, clothing, custom…) and pack sizes / extra barcodes.
import { shelfLifeLabel } from '@ims/core'
import type { JsonValue, ProductCodeRow, ProductRow } from '@ims/data'
import { Button, Card, Checkbox, Field, IconButton, Input, Select } from '@ims/ui'
import { Plus, Trash2 } from '@ims/ui/icons'
import { MoneyInput, QtyInput, type Lookups } from '../components.tsx'
import {
  FIELD_GROUPS,
  formatAttribute,
  type AttributeField,
  type FieldGroup,
} from '../lib/catalog.ts'
import { money, qty } from '../lib/format.ts'

export function SectionTitle({ children }: { children: string }) {
  return (
    <h3 className="-mb-1 mt-2 flex items-center gap-3 text-section text-muted after:h-px after:flex-1 after:bg-border sm:col-span-2">
      {children}
    </h3>
  )
}

export function BrandField({
  lookups,
  brandId,
  onBrand,
  newBrandName,
  onNewBrandName,
}: {
  lookups: Lookups
  brandId: string
  onBrand: (id: string) => void
  newBrandName: string
  onNewBrandName: (name: string) => void
}) {
  return (
    <>
      <Field label="Brand">
        {(id) => (
          <Select id={id} value={brandId} onChange={(e) => onBrand(e.target.value)}>
            <option value="">—</option>
            {lookups.brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
            <option value="__new">+ New brand…</option>
          </Select>
        )}
      </Field>
      {brandId === '__new' && (
        <Field label="New brand name">
          {(id) => (
            <Input id={id} value={newBrandName} onChange={(e) => onNewBrandName(e.target.value)} />
          )}
        </Field>
      )}
    </>
  )
}

export function AttributeFields({
  sections,
  values,
  onChange,
}: {
  sections: { group: FieldGroup; fields: AttributeField[] }[]
  values: Record<string, JsonValue | undefined>
  onChange: (key: string, value: JsonValue | undefined) => void
}) {
  return (
    <>
      {sections.map(({ group, fields }) => (
        <div key={group.id} className="contents">
          <SectionTitle>{group.label}</SectionTitle>
          {fields.map((f) => (
            <AttributeInput
              key={f.key}
              field={f}
              value={values[f.key]}
              onChange={(v) => onChange(f.key, v)}
            />
          ))}
        </div>
      ))}
    </>
  )
}

function AttributeInput({
  field,
  value,
  onChange,
}: {
  field: AttributeField
  value: JsonValue | undefined
  onChange: (value: JsonValue | undefined) => void
}) {
  if (field.type === 'yesno') {
    return (
      <div className="flex items-end pb-2">
        <Checkbox
          label={field.label}
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
        />
      </div>
    )
  }
  return (
    <Field label={field.label}>
      {(id) =>
        field.type === 'select' ? (
          <Select
            id={id}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value || undefined)}
          >
            <option value="">—</option>
            {(field.options ?? []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </Select>
        ) : (
          <Input
            id={id}
            type={field.type === 'date' ? 'date' : 'text'}
            inputMode={field.type === 'number' ? 'decimal' : undefined}
            placeholder={field.placeholder}
            value={value == null ? '' : String(value)}
            onChange={(e) => {
              const text = e.target.value
              if (field.type === 'number') {
                const n = Number(text)
                onChange(text.trim() === '' ? undefined : Number.isFinite(n) ? n : text)
              } else onChange(text)
            }}
          />
        )
      }
    </Field>
  )
}

/** A pack size or extra barcode being edited (id set when it already exists). */
export interface PackDraft {
  key: string
  id: string | null
  code: string
  label: string
  qtyMilli: number | null
  pricePaisa: number | null
  removed: boolean
}

export function packDrafts(codes: ProductCodeRow[]): PackDraft[] {
  return codes.map((c) => ({
    key: c.id,
    id: c.id,
    code: c.code,
    label: c.label ?? '',
    qtyMilli: c.qty_milli,
    pricePaisa: c.sell_price_paisa,
    removed: false,
  }))
}

export function PacksEditor({
  packs,
  onChange,
  allowsDecimal,
  unitSymbol,
}: {
  packs: PackDraft[]
  onChange: (packs: PackDraft[]) => void
  allowsDecimal: boolean
  unitSymbol: string
}) {
  const update = (key: string, change: Partial<PackDraft>) =>
    onChange(packs.map((p) => (p.key === key ? { ...p, ...change } : p)))
  const visible = packs.filter((p) => !p.removed)
  return (
    <div className="space-y-2 sm:col-span-2">
      <p className="text-xs text-muted">
        Scanning a pack barcode adds its quantity. Leave the pack price empty to charge the unit
        price × quantity. Use quantity 1 for an extra barcode of the same item.
      </p>
      {visible.map((p) => (
        <div
          key={p.key}
          className="grid grid-cols-2 items-end gap-2 rounded-xl border border-border p-2 sm:grid-cols-[1.4fr_1fr_0.8fr_1fr_auto]"
        >
          <Field label="Barcode">
            {(id) => (
              <Input
                id={id}
                value={p.code}
                onChange={(e) => update(p.key, { code: e.target.value })}
              />
            )}
          </Field>
          <Field label="Pack name">
            {(id) => (
              <Input
                id={id}
                value={p.label}
                placeholder="Box of 12"
                onChange={(e) => update(p.key, { label: e.target.value })}
              />
            )}
          </Field>
          <Field label={`Qty${unitSymbol ? ` (${unitSymbol})` : ''}`}>
            {(id) => (
              <QtyInput
                id={id}
                milli={p.qtyMilli}
                allowsDecimal={allowsDecimal}
                onValue={(m) => update(p.key, { qtyMilli: m })}
              />
            )}
          </Field>
          <Field label="Pack price">
            {(id) => (
              <MoneyInput
                id={id}
                paisa={p.pricePaisa}
                placeholder="optional"
                onValue={(v) => update(p.key, { pricePaisa: v })}
              />
            )}
          </Field>
          <IconButton
            label={`Remove ${p.label || p.code || 'pack'}`}
            onClick={() =>
              p.id
                ? update(p.key, { removed: true })
                : onChange(packs.filter((x) => x.key !== p.key))
            }
            className="mb-1"
          >
            <Trash2 className="size-4" />
          </IconButton>
        </div>
      ))}
      <Button
        size="sm"
        variant="secondary"
        icon={<Plus className="size-3.5" />}
        onClick={() =>
          onChange([
            ...packs,
            {
              key: crypto.randomUUID(),
              id: null,
              code: '',
              label: '',
              qtyMilli: null,
              pricePaisa: null,
              removed: false,
            },
          ])
        }
      >
        Add pack or barcode
      </Button>
    </div>
  )
}

/** Everything extra about a product that has a value (whether or not its group is shown now). */
export function ProductDetailsCard({
  product,
  lookups,
  codes,
  custom,
  unitSymbol,
  settings,
  side,
  showPrice,
}: {
  product: ProductRow
  lookups: Lookups
  codes: ProductCodeRow[]
  custom: AttributeField[]
  unitSymbol: string
  settings: Parameters<typeof money>[1]
  /** In the page's side column: one label and value per line. */
  side?: boolean
  /** The selling price too (the stock view has no price card). */
  showPrice?: boolean
}) {
  const attrs =
    product.attributes &&
    typeof product.attributes === 'object' &&
    !Array.isArray(product.attributes)
      ? product.attributes
      : {}
  const known = [...FIELD_GROUPS.flatMap((g) => g.attributes ?? []), ...custom]
  const rows: [string, string][] = []
  const add = (label: string, value: string | null | undefined) => {
    if (value) rows.push([label, value])
  }
  add('Category', lookups.category(product.category_id)?.name)
  if (side) {
    add('Unit', lookups.unit(product.unit_id)?.name)
    add('VAT', lookups.taxRate(product.tax_rate_id)?.name ?? 'None')
  }
  if (showPrice && product.for_sale) add('Selling price', money(product.sell_price_paisa, settings))
  add('Brand', lookups.brand(product.brand_id)?.name)
  add('Manufacturer', product.manufacturer)
  add('MRP', product.mrp_paisa !== null ? money(product.mrp_paisa, settings) : null)
  add(
    'Wholesale price',
    product.wholesale_price_paisa !== null ? money(product.wholesale_price_paisa, settings) : null,
  )
  add(
    'Minimum price',
    product.min_price_paisa !== null ? money(product.min_price_paisa, settings) : null,
  )
  add('Scale item code (PLU)', product.plu_code)
  add('HS code', product.hs_code)
  add('Shelf / rack', product.shelf_location)
  add(
    'Shelf life',
    product.track_expiry && product.shelf_life_days
      ? shelfLifeLabel(product.shelf_life_days, product.shelf_life_unit)
      : null,
  )
  add('Tags', product.tags)
  add(
    'Reorder quantity',
    product.reorder_qty_milli ? `${qty(product.reorder_qty_milli)} ${unitSymbol}`.trim() : null,
  )
  for (const [key, value] of Object.entries(attrs)) {
    const field = known.find((f) => f.key === key)
    add(field?.label ?? key, formatAttribute(field ?? { key, label: key, type: 'text' }, value))
  }
  if (rows.length === 0 && codes.length === 0) return null
  return (
    <Card className={side ? undefined : 'mt-4'}>
      <h2 className="mb-3 font-semibold">Details</h2>
      {rows.length > 0 &&
        (side ? (
          <dl className="divide-y divide-border text-sm">
            {rows.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3 py-1.5">
                <dt className="text-muted">{label}</dt>
                <dd className="text-right font-semibold">{value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
            {rows.map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs text-muted">{label}</dt>
                <dd className="font-medium">{value}</dd>
              </div>
            ))}
          </dl>
        ))}
      {codes.length > 0 && (
        <div className="mt-4">
          <p className="mb-1 text-xs text-muted">Pack sizes & extra barcodes</p>
          <ul className="divide-y divide-border rounded-xl border border-border text-sm">
            {codes.map((c) => (
              <li key={c.id} className="flex flex-wrap justify-between gap-2 px-3 py-2">
                <span>
                  <span className="font-medium">{c.label || 'Extra barcode'}</span>
                  <span className="ml-2 font-mono text-xs text-muted">{c.code}</span>
                </span>
                <span className="tabular text-muted">
                  {qty(c.qty_milli)} {unitSymbol}
                  {c.sell_price_paisa !== null ? ` · ${money(c.sell_price_paisa, settings)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  )
}
